import React, { useState, useMemo } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { OrderEmailButton } from "./OrderEmailButton";
import { ShipOrderDialog } from "./ShipOrderDialog";

export const OrderStatusBadge = ({ status }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'pending':
        return { label: 'In Attesa', color: 'bg-yellow-100 text-yellow-800' };
      case 'confirmed':
        return { label: 'Confermato', color: 'bg-blue-100 text-blue-800' };
      case 'shipped':
        return { label: 'Spedito', color: 'bg-purple-100 text-purple-800' };
      case 'partially_shipped':
        return { label: 'Spedito parzialmente', color: 'bg-orange-100 text-orange-800' };
      case 'delivered':
        return { label: 'Consegnato', color: 'bg-green-100 text-green-800' };
      case 'cancelled':
        return { label: 'Annullato', color: 'bg-red-100 text-red-800' };
      default:
        return { label: status, color: 'bg-gray-100 text-gray-800' };
    }
  };

  const config = getStatusConfig(status);
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

// Order Actions Component
export const OrderActions = ({ order, onStatusUpdate, onDuplicate, shippingRules, onRefresh }) => {
  const [shipResult, setShipResult] = useState('');
  const [showTrackingInput, setShowTrackingInput] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Cancellation dialog state — reason + send-email toggle shown before
  // actually marking the order as cancelled.
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelSendEmail, setCancelSendEmail] = useState(true);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  // Shipping top-up dialog — asks customer to pay a shipping delta via a
  // Custom Payment Link, sends a localized email (IT/EN/DE/FR auto-detect).
  const [showTopupDialog, setShowTopupDialog] = useState(false);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupCountry, setTopupCountry] = useState("");
  const [topupDays, setTopupDays] = useState(2);
  const [topupMessage, setTopupMessage] = useState("");
  const [topupPaymentMethod, setTopupPaymentMethod] = useState("card");
  const [topupSubmitting, setTopupSubmitting] = useState(false);
  const [topupResult, setTopupResult] = useState(null);
  // Mark-as-paid manual dialog (for bank transfers)
  const [showMarkPaidDialog, setShowMarkPaidDialog] = useState(false);
  const [markPaidRef, setMarkPaidRef] = useState("");
  const [markPaidNote, setMarkPaidNote] = useState("");
  const [markPaidSubmitting, setMarkPaidSubmitting] = useState(false);
  const [reviewSending, setReviewSending] = useState(false);
  const [reviewSent, setReviewSent] = useState(false);
  const [reminderCount, setReminderCount] = useState(order.review_reminder_count || 0);
  // Edit customer-info dialog (admin can correct name/email/phone/address typos)
  const [showEditCustomerDialog, setShowEditCustomerDialog] = useState(false);
  const [editCustForm, setEditCustForm] = useState({
    name: "", email: "", phone: "", address: "", city: "", zipCode: "",
    country: "", specificCountry: "", notes: "",
  });
  const [editCustSubmitting, setEditCustSubmitting] = useState(false);
  const [editCustError, setEditCustError] = useState("");
  // Edit order items dialog (swap/add/remove articles on an existing order)
  const [showEditItemsDialog, setShowEditItemsDialog] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editItemsSearch, setEditItemsSearch] = useState("");
  const [editItemsCatalog, setEditItemsCatalog] = useState([]);
  const [editItemsSubmitting, setEditItemsSubmitting] = useState(false);
  const [editItemsError, setEditItemsError] = useState("");
  const [showPacklinkModal, setShowPacklinkModal] = useState(false);
  const [packlinkServices, setPacklinkServices] = useState([]);
  // Memoized: cheapest 10 packlink services (sorted by base_price ascending)
  const packlinkServicesCheapest10 = useMemo(
    () => [...packlinkServices]
      .sort((a, b) => parseFloat(a.base_price || 0) - parseFloat(b.base_price || 0))
      .slice(0, 10),
    [packlinkServices]
  );
  const [packlinkLoading, setPacklinkLoading] = useState(false);
  const [packlinkError, setPacklinkError] = useState("");
  const [packlinkCreating, setPacklinkCreating] = useState(false);
  // Estimated package weight from items (fallback 0.6kg per item)
  const _estWeight = Math.max(1, Math.ceil((order.items || []).reduce((s, i) => s + ((i.weight || 0.6) * (i.quantity || 1)), 0)));
  // Editable parcel inputs (admin can override before querying Packlink)
  const [parcelWeight, setParcelWeight] = useState(_estWeight);
  const [parcelWidth, setParcelWidth] = useState(30);
  const [parcelHeight, setParcelHeight] = useState(20);
  const [parcelLength, setParcelLength] = useState(15);
  // Has the user already submitted parcel dimensions? (controls modal flow)
  const [packlinkParamsSubmitted, setPacklinkParamsSubmitted] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Open the "edit customer info" dialog pre-filled with the current values.
  const openEditCustomerDialog = () => {
    const ci = order.customer_info || {};
    setEditCustForm({
      name: ci.name || "",
      email: ci.email || "",
      phone: ci.phone || "",
      address: ci.address || "",
      city: ci.city || "",
      zipCode: ci.zipCode || ci.postal_code || "",
      country: ci.country || "italia",
      specificCountry: ci.specificCountry || "",
      notes: ci.notes || "",
    });
    setEditCustError("");
    setShowEditCustomerDialog(true);
  };

  // Open the "edit order items" dialog with normalized current items + catalog.
  const openEditItemsDialog = async () => {
    const normalized = (order.items || []).map((it) => ({
      article_id: it.article_id,
      quantity: it.quantity || 1,
      title: it.title || it.article?.title || it.article_id,
      price: it.price ?? it.article?.price ?? 0,
    }));
    setEditItems(normalized);
    setEditItemsSearch("");
    setEditItemsError("");
    setShowEditItemsDialog(true);
    try {
      const res = await axios.get(`${API}/articles`);
      setEditItemsCatalog(res.data || []);
    } catch (e) {
      setEditItemsError("Impossibile caricare il catalogo articoli");
    }
  };

  const editItemsNewTotal = (() => {
    const itemsTotal = editItems.reduce((s, i) => s + (i.price || 0) * (i.quantity || 0), 0);
    return Math.max(0, itemsTotal + (order.shipping_cost || 0) - (order.discount_amount || 0));
  })();

  const submitEditItems = async () => {
    if (editItems.length === 0) {
      setEditItemsError("L'ordine deve contenere almeno un articolo");
      return;
    }
    setEditItemsSubmitting(true);
    setEditItemsError("");
    try {
      await axios.put(`${API}/orders/${order.id}/items`, { items: editItems.map(({ _origPrice, ...it }) => it) });
      setShowEditItemsDialog(false);
      if (onStatusUpdate) onStatusUpdate();
    } catch (e) {
      setEditItemsError(e?.response?.data?.detail || e.message || "Errore salvataggio");
    } finally {
      setEditItemsSubmitting(false);
    }
  };

  const submitEditCustomer = async () => {
    setEditCustSubmitting(true);
    setEditCustError("");
    try {
      await axios.put(`${API}/orders/${order.id}/customer-info`, editCustForm);
      setShowEditCustomerDialog(false);
      // Refresh the orders list so the new values appear.
      if (onStatusUpdate) onStatusUpdate();
    } catch (e) {
      setEditCustError(e?.response?.data?.detail || e.message || "Errore salvataggio");
    } finally {
      setEditCustSubmitting(false);
    }
  };

  // Country name to ISO code mapping
  const countryToISO = (name) => {
    const map = { 'italia': 'IT', 'italy': 'IT', 'germania': 'DE', 'germany': 'DE', 'francia': 'FR', 'france': 'FR',
      'spagna': 'ES', 'spain': 'ES', 'portogallo': 'PT', 'portugal': 'PT', 'regno unito': 'GB', 'uk': 'GB', 'united kingdom': 'GB',
      'paesi bassi': 'NL', 'netherlands': 'NL', 'olanda': 'NL', 'holland': 'NL', 'belgio': 'BE', 'belgium': 'BE', 'austria': 'AT',
      'svizzera': 'CH', 'switzerland': 'CH', 'polonia': 'PL', 'poland': 'PL', 'romania': 'RO', 'lussemburgo': 'LU', 'luxembourg': 'LU',
      'irlanda': 'IE', 'ireland': 'IE', 'stati uniti': 'US', 'usa': 'US', 'united states': 'US',
      'brasile': 'BR', 'brazil': 'BR', 'argentina': 'AR', 'india': 'IN', 'hong kong': 'HK',
      'cina': 'CN', 'china': 'CN', 'giappone': 'JP', 'japan': 'JP',
      'corea del sud': 'KR', 'south korea': 'KR', 'bielorussia': 'BY', 'belarus': 'BY', 'russia': 'RU',
      'grecia': 'GR', 'greece': 'GR', 'svezia': 'SE', 'sweden': 'SE', 'norvegia': 'NO', 'norway': 'NO',
      'danimarca': 'DK', 'denmark': 'DK', 'finlandia': 'FI', 'finland': 'FI', 'croazia': 'HR', 'croatia': 'HR',
      'ungheria': 'HU', 'hungary': 'HU', 'repubblica ceca': 'CZ', 'czech republic': 'CZ',
      'slovacchia': 'SK', 'slovakia': 'SK', 'slovenia': 'SI', 'bulgaria': 'BG',
      'estonia': 'EE', 'lettonia': 'LV', 'latvia': 'LV', 'lituania': 'LT', 'lithuania': 'LT',
      'cipro': 'CY', 'cyprus': 'CY', 'malta': 'MT', 'turchia': 'TR', 'turkey': 'TR',
      'messico': 'MX', 'mexico': 'MX', 'canada': 'CA', 'australia': 'AU', 'nuova zelanda': 'NZ', 'new zealand': 'NZ',
      'sudafrica': 'ZA', 'south africa': 'ZA', 'marocco': 'MA', 'morocco': 'MA', 'egitto': 'EG', 'egypt': 'EG',
      'israele': 'IL', 'israel': 'IL', 'emirati arabi': 'AE', 'uae': 'AE', 'tailandia': 'TH', 'thailand': 'TH',
      'singapore': 'SG', 'malesia': 'MY', 'malaysia': 'MY', 'indonesia': 'ID', 'filippine': 'PH', 'philippines': 'PH',
      'colombia': 'CO', 'cile': 'CL', 'chile': 'CL', 'peru': 'PE', 'venezuela': 'VE',
      'polska': 'PL', 'deutschland': 'DE', 'españa': 'ES', 'nederland': 'NL', 'belgique': 'BE',
      'österreich': 'AT', 'schweiz': 'CH', 'suisse': 'CH', 'hrvatska': 'HR', 'magyarország': 'HU',
      'česko': 'CZ', 'slovensko': 'SK', 'slovenija': 'SI', 'българия': 'BG', 'eesti': 'EE',
      'latvija': 'LV', 'lietuva': 'LT', 'türkiye': 'TR', 'méxico': 'MX', 'brasil': 'BR',
      'беларусь': 'BY', 'белоруссия': 'BY', 'россия': 'RU', 'україна': 'UA', 'ucraina': 'UA', 'ukraine': 'UA',
      'ελλάδα': 'GR', 'srbija': 'RS', 'serbia': 'RS', 'danmark': 'DK', 'norge': 'NO', 'sverige': 'SE', 'suomi': 'FI',
      'perù': 'PE', 'lëtzebuerg': 'LU', 'belgien': 'BE', 'românia': 'RO', 'cechia': 'CZ',
      'niemcy': 'DE', 'francja': 'FR', 'włochy': 'IT', 'hiszpania': 'ES', 'holandia': 'NL',
      'wielka brytania': 'GB', 'anglia': 'GB', 'irlandia': 'IE', 'portugalia': 'PT', 'grecja': 'GR',
      'szwecja': 'SE', 'norwegia': 'NO', 'dania': 'DK', 'finlandia': 'FI', 'austria': 'AT',
      'szwajcaria': 'CH', 'belgia': 'BE', 'czechy': 'CZ', 'słowacja': 'SK', 'węgry': 'HU',
      'chorwacja': 'HR', 'rumunia': 'RO', 'bułgaria': 'BG', 'litwa': 'LT', 'łotwa': 'LV',
      'rosja': 'RU', 'białoruś': 'BY', 'ukraina': 'UA', 'turcja': 'TR', 'stany zjednoczone': 'US',
      'brazylia': 'BR', 'argentyna': 'AR', 'indie': 'IN', 'chiny': 'CN', 'japonia': 'JP',
      'korea południowa': 'KR', 'meksyk': 'MX', 'kanada': 'CA',
      'allemagne': 'DE', 'espagne': 'ES', 'italie': 'IT', 'pays-bas': 'NL', 'royaume-uni': 'GB',
      'irlande': 'IE', 'suède': 'SE', 'norvège': 'NO', 'danemark': 'DK', 'finlande': 'FI',
      'autriche': 'AT', 'pologne': 'PL', 'roumanie': 'RO', 'hongrie': 'HU', 'croatie': 'HR',
      'grèce': 'GR', 'turquie': 'TR', 'russie': 'RU', 'états-unis': 'US', 'brésil': 'BR',
      'argentine': 'AR', 'inde': 'IN', 'chine': 'CN', 'japon': 'JP', 'mexique': 'MX',
      'alemania': 'DE', 'francia': 'FR', 'países bajos': 'NL', 'reino unido': 'GB',
      'suecia': 'SE', 'noruega': 'NO', 'dinamarca': 'DK', 'hungría': 'HU', 'croacia': 'HR',
      'rumania': 'RO', 'turquía': 'TR', 'estados unidos': 'US', 'japón': 'JP',
      '中国': 'CN', '日本': 'JP', '한국': 'KR',
    };
    const lower = (name || '').toLowerCase().trim();
    return map[lower] || (lower.length === 2 ? lower.toUpperCase() : 'IT');
  };

  const fetchPacklinkServices = async () => {
    const ci = order.customer_info || {};
    
    // Load shipping rules fresh to resolve UUID
    let rules = shippingRules || [];
    if (!rules.length) {
      try {
        const rulesRes = await axios.get(`${API}/shipping-rules`);
        rules = rulesRes.data.shipping_rules || rulesRes.data || [];
      } catch (e) {
        // Non-blocking: shipping-rules fetch is only used to resolve country
        // UUIDs. If it fails the country falls back to ci.country raw value
        // and the user still sees the order details (slightly worse country label).
        console.warn("Could not resolve country via /shipping-rules:", e?.message || e);
      }
    }
    
    // Resolve country: specificCountry > resolve UUID from shipping rules > country field
    let countryName = ci.specificCountry || '';
    if (!countryName) {
      const countryField = ci.country || '';
      const rule = rules.find(r => r.id === countryField);
      if (rule) {
        const zoneName = (rule.zone || '').toLowerCase();
        countryName = (zoneName === 'national') ? 'Italia' : (rule.country || '').replace(/[^\w\s]/g, '').trim();
      } else {
        const fallback = countryField.toLowerCase();
        const zoneMap = { 'nazionale': 'Italia', 'national': 'Italia', 'europa': '', 'internazionale': '' };
        countryName = zoneMap[fallback] !== undefined ? (zoneMap[fallback] || countryField) : countryField;
      }
    }
    if (!countryName) countryName = 'Italia';
    const toCountry = countryToISO(countryName);
    
    // Extract ZIP: check all possible fields, then look in city/address
    let toZip = (ci.zipCode || ci.postal_code || ci.zip_code || ci.zip || '').toString().replace(/\s/g, '').trim();
    if (!toZip) {
      const searchText = `${ci.city || ''} ${ci.address || ''}`;
      const zipMatch = searchText.match(/\b(\d{2}[-]\d{3})\b|\b(\d{5})\b|\b(\d{4,6})\b/i);
      if (zipMatch) toZip = (zipMatch[1] || zipMatch[2] || zipMatch[3]).trim();
    }
    if (!toZip) toZip = toCountry === 'IT' ? '00100' : '00000';
    
    const weight = Math.max(0.1, parseFloat(parcelWeight) || _estWeight);
    const w = Math.max(1, parseInt(parcelWidth) || 30);
    const h = Math.max(1, parseInt(parcelHeight) || 20);
    const l = Math.max(1, parseInt(parcelLength) || 15);
    setPacklinkLoading(true);
    setPacklinkError("");
    setPacklinkServices([]);
    try {
      const access_token = sessionStorage.getItem('access_token');
      const response = await axios.get(`${API}/packlink/services`, {
        params: { from_zip: '80010', from_country: 'IT', to_zip: toZip || '00000', to_country: toCountry, weight, width: w, height: h, length: l },
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setPacklinkServices(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.includes("400") || detail.includes("Bad Request") || detail.includes("Errore Packlink")) {
        setPacklinkError(`Packlink non supporta la spedizione verso ${ci.specificCountry || ci.country || toCountry} con queste dimensioni/peso. Verifica peso/dimensioni o usa "Apri Packlink PRO manualmente".`);
      } else {
        setPacklinkError(detail || "Errore nel recupero servizi Packlink");
      }
    } finally {
      setPacklinkLoading(false);
    }
  };

  const createPacklinkShipment = async (serviceId, serviceName) => {
    const ci = order.customer_info || {};
    const toCountry = countryToISO(ci.specificCountry || ci.country);
    setPacklinkCreating(true);
    setPacklinkError("");
    try {
      const access_token = sessionStorage.getItem('access_token');
      const nameParts = (ci.name || '').split(' ');
      const shipmentData = {
        order_id: order.id,
        // Pass the carrier name explicitly so the backend can persist it on
        // the order document (the Packlink API response doesn't always echo
        // the carrier; we want to remember it for the admin orders list).
        carrier_name: serviceName,
        // Packlink REST v1 expects `first_name` / `last_name` (NOT
        // `name` / `surname` — those get silently dropped, which is why
        // shipments showed up on packlink.com as "INCOMPLETO / Indirizzo
        // mancante" even though we passed the customer info from the order).
        from: { country: "IT", zip: "80010", city: "Villaricca", street1: "Corso Europa 175", first_name: "Massimiliano", last_name: "Cerasuolo", name: "Massimiliano", surname: "Cerasuolo", phone: "+393470808245", email: "amm@edicolavillaricca.com" },
        to: { country: toCountry, zip: (ci.zipCode || '').replace(/\s/g, ''), city: ci.city || '', street1: ci.address || '', first_name: nameParts[0] || '', last_name: nameParts.slice(1).join(' ') || '', name: nameParts[0] || '', surname: nameParts.slice(1).join(' ') || '', phone: ci.phone || '', email: ci.email || '' },
        packages: [{ weight: Math.max(0.1, parseFloat(parcelWeight) || _estWeight), width: Math.max(1, parseInt(parcelWidth) || 30), height: Math.max(1, parseInt(parcelHeight) || 20), length: Math.max(1, parseInt(parcelLength) || 15) }],
        service_id: serviceId,
        content: "Modellismo e Collezionismo",
        contentvalue: order.total_amount || 0,
        source: "custom"
      };
      await axios.post(`${API}/packlink/shipment`, shipmentData, { headers: { Authorization: `Bearer ${access_token}` } });
      setShowPacklinkModal(false);
      alert(`Spedizione creata su Packlink con ${serviceName}!\nVai su pro.packlink.com per completare e stampare l'etichetta.`);
    } catch (err) {
      setPacklinkError(err.response?.data?.detail || "Errore creazione spedizione");
    } finally {
      setPacklinkCreating(false);
    }
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      'cash': 'Contrassegno',
      'transfer': 'Bonifico Bancario',
      'paypal': 'PayPal',
      'card': 'Carta di Credito',
      'stripe': 'Carta di Credito'
    };
    return methods[method] || method;
  };

  const handleSendReviewReminder = async () => {
    setReviewSending(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const res = await axios.post(`${API}/orders/${order.id}/send-review-reminder`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminderCount(res.data.review_reminder_count || reminderCount + 1);
      setReviewSent(true);
      setTimeout(() => setReviewSent(false), 5000);
    } catch (err) {
      alert(err.response?.data?.detail || "Errore nell'invio del promemoria");
    } finally {
      setReviewSending(false);
    }
  };

  const getStatusLabel = (status) => {
    const statuses = {
      'pending': 'In Attesa',
      'confirmed': 'Confermato',
      'shipped': 'Spedito',
      'partially_shipped': 'Spedito parzialmente',
      'delivered': 'Consegnato',
      'cancelled': 'Annullato'
    };
    return statuses[status] || status;
  };

  const handlePrintOrder = () => {
    const orderDate = new Date(order.order_date).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Ordine #${order.id.slice(-8)}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            max-width: 800px;
            margin: 20px auto;
            padding: 20px;
            color: #333;
          }
          .header {
            text-align: center;
            border-bottom: 3px solid #000;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .header h1 {
            margin: 0;
            font-size: 28px;
          }
          .section {
            margin-bottom: 25px;
          }
          .section-title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 10px;
            color: #000;
            border-bottom: 2px solid #ddd;
            padding-bottom: 5px;
          }
          .info-row {
            display: flex;
            padding: 8px 0;
            border-bottom: 1px solid #eee;
          }
          .info-label {
            font-weight: bold;
            width: 150px;
          }
          .info-value {
            flex: 1;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          th, td {
            padding: 12px;
            text-align: left;
            border-bottom: 1px solid #ddd;
          }
          th {
            background-color: #f5f5f5;
            font-weight: bold;
          }
          .total-row {
            font-weight: bold;
            font-size: 18px;
            background-color: #f9f9f9;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #000;
            text-align: center;
            font-size: 12px;
            color: #666;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>ORDINE #${order.id.slice(-8).toUpperCase()}</h1>
          <p style="margin: 10px 0 0 0; font-size: 14px;">${orderDate}</p>
        </div>

        <div class="section">
          <div class="section-title">📋 Dettagli Ordine</div>
          <div class="info-row">
            <div class="info-label">Stato:</div>
            <div class="info-value">${getStatusLabel(order.status)}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Metodo Pagamento:</div>
            <div class="info-value">${getPaymentMethodLabel(order.payment_method)}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Stato Pagamento:</div>
            <div class="info-value">${order.payment_status === 'paid' ? 'Pagato' : 'In Attesa'}</div>
          </div>
          <div style="background-color: #e8f5e9; font-weight: bold; padding: 12px 8px; margin: 10px 0; border-radius: 4px; display: flex;">
            <div style="white-space: nowrap; min-width: 220px; font-weight: bold;">💰 Importo Totale Pagato:</div>
            <div style="color: #2e7d32; font-size: 18px; font-weight: bold;">€${(order.total_amount || 0).toFixed(2)}</div>
          </div>
          <div style="background-color: #fff3e0; font-weight: bold; padding: 10px 8px; margin: 10px 0; border-radius: 4px; display: flex;">
            <div style="white-space: nowrap; min-width: 220px; font-weight: bold;">🚚 Costo Spedizione:</div>
            <div style="color: #e65100; font-size: 16px; font-weight: bold;">€${(order.shipping_cost || 0).toFixed(2)}</div>
          </div>
          ${order.shipping_method === 'inpost' && order.inpost_pickup_point ? `
          <div style="background-color: #e3f2fd; font-weight: bold; padding: 10px 8px; margin: 10px 0; border-radius: 4px;">
            <div style="font-weight: bold; margin-bottom: 5px;">📦 SPEDIZIONE INPOST:</div>
            <div style="font-size: 14px; color: #1565c0;">
              <strong>Punto di Ritiro:</strong> ${order.inpost_pickup_point}
            </div>
            ${order.inpost_insurance ? '<div style="font-size: 14px; color: #1565c0; margin-top: 5px;"><strong>🛡️ Assicurazione:</strong> Inclusa (+€3)</div>' : ''}
          </div>
          ` : ''}
          ${order.tracking_number ? `
          <div class="info-row">
            <div class="info-label">Tracking:</div>
            <div class="info-value">${order.tracking_number}</div>
          </div>
          ` : ''}
        </div>

        <div class="section">
          <div class="section-title">👤 Dati Cliente</div>
          <div class="info-row">
            <div class="info-label">Nome:</div>
            <div class="info-value">${order.customer_info.name}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Email:</div>
            <div class="info-value">${order.customer_info.email}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Telefono:</div>
            <div class="info-value">${order.customer_info.phone || 'N/A'}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Indirizzo:</div>
            <div class="info-value">${order.customer_info.address}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Città:</div>
            <div class="info-value">${order.customer_info.city} ${order.customer_info.zipCode || order.customer_info.postal_code || ''}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Paese:</div>
            <div class="info-value">${order.customer_info.specificCountry || order.customer_info.country || 'Italia'}</div>
          </div>
          ${order.customer_info.notes ? `
          <div class="info-row">
            <div class="info-label">Note:</div>
            <div class="info-value">${order.customer_info.notes}</div>
          </div>
          ` : ''}
        </div>

        <div class="section">
          <div class="section-title">📦 Prodotti Ordinati</div>
          <table>
            <thead>
              <tr>
                <th>Prodotto</th>
                <th>Quantità</th>
                <th>Prezzo Unit.</th>
                <th>Totale</th>
              </tr>
            </thead>
            <tbody>
              ${order.items.map(item => `
                <tr>
                  <td>${item.title || item.article?.title || 'Prodotto'}</td>
                  <td>${item.quantity}</td>
                  <td>€${(item.price || item.article?.price || 0).toFixed(2)}</td>
                  <td>€${((item.price || item.article?.price || 0) * item.quantity).toFixed(2)}</td>
                </tr>
              `).join('')}
              <tr>
                <td colspan="3" style="text-align: right; font-weight: bold;">Subtotale Prodotti:</td>
                <td>€${order.items.reduce((sum, item) => sum + (item.price || item.article?.price || 0) * item.quantity, 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td colspan="3" style="text-align: right; font-weight: bold;">Spese Spedizione:</td>
                <td>€${(order.shipping_cost || 0).toFixed(2)}</td>
              </tr>
              <tr class="total-row">
                <td colspan="3" style="text-align: right;">TOTALE ORDINE:</td>
                <td><strong>€${(order.items.reduce((sum, item) => sum + (item.price || item.article?.price || 0) * item.quantity, 0) + (order.shipping_cost || 0)).toFixed(2)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="footer">
          <p>Grazie per il tuo ordine!</p>
          <p>Per qualsiasi informazione contattaci</p>
        </div>

        <div class="no-print" style="text-align: center; margin-top: 30px;">
          <button onclick="window.print()" style="padding: 10px 30px; font-size: 16px; cursor: pointer; background-color: #4CAF50; color: white; border: none; border-radius: 5px;">
            🖨️ Stampa
          </button>
          <button onclick="window.close()" style="padding: 10px 30px; font-size: 16px; cursor: pointer; background-color: #f44336; color: white; border: none; border-radius: 5px; margin-left: 10px;">
            ✕ Chiudi
          </button>
        </div>
      </body>
      </html>
    `;

    const printIframe = document.createElement('iframe');
    printIframe.style.position = 'absolute';
    printIframe.style.top = '-10000px';
    document.body.appendChild(printIframe);
    const iframeDoc = printIframe.contentDocument || printIframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(printContent);
    iframeDoc.close();
    printIframe.contentWindow.focus();
    printIframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(printIframe), 1000);
  };

  const handleStatusChange = (newStatus) => {
    if (newStatus === 'shipped') {
      setShowTrackingInput(true);
      return;
    }
    // Cancellation requires confirmation: open dialog asking for reason +
    // whether to notify the customer by email.
    if (newStatus === 'cancelled') {
      setCancelReason("");
      setCancelSendEmail(true);
      setShowCancelDialog(true);
      return;
    }
    onStatusUpdate(order.id, newStatus, order.tracking_number || null);
    setShowTrackingInput(false);
  };

  const confirmCancellation = async () => {
    setCancelSubmitting(true);
    try {
      await onStatusUpdate(order.id, 'cancelled', null, {
        reason: cancelReason,
        sendEmail: cancelSendEmail,
      });
      setShowCancelDialog(false);
    } finally {
      setCancelSubmitting(false);
    }
  };

  const openTopupDialog = async () => {
    // Pre-fill country if customer info hints at foreign address
    const ci = order.customer_info || {};
    const city = (ci.city || "").toLowerCase();
    const zip = (ci.zipCode || ci.zip_code || "").toString();
    let detectedCountry = "";
    const hasLetter = /[a-zA-Z]/.test(zip);
    if (/burg|dorf|hausen|heim|stadt|bach|berg|feld|stein|ingen|kirchen/.test(city)) detectedCountry = "Germania";
    else if (hasLetter && /\d/.test(zip)) detectedCountry = "Regno Unito";
    else if (/-sur-|-lès-|-les-|-en-|-aux/.test(city)) detectedCountry = "Francia";
    else if (/dam|veen|haag/.test(city)) detectedCountry = "Paesi Bassi";
    setTopupCountry(detectedCountry);
    setTopupAmount("");
    setTopupDays(2);
    setTopupMessage("");
    setTopupPaymentMethod("card");
    setTopupResult(null);
    setShowTopupDialog(true);

    // Async: ask backend to suggest the topup amount based on existing rules
    try {
      const access_token = sessionStorage.getItem("access_token");
      const resp = await axios.get(
        `${API}/admin/orders/${order.id}/suggest-topup`,
        {
          params: detectedCountry ? { country: detectedCountry } : {},
          headers: { Authorization: `Bearer ${access_token}` },
        }
      );
      const suggested = resp.data?.suggested_delta;
      if (suggested && suggested > 0) {
        setTopupAmount(String(suggested.toFixed(2)));
      }
      // Auto-fill detected country if backend found one we missed
      if (!detectedCountry && resp.data?.detected_country) {
        const c = resp.data.detected_country;
        setTopupCountry(c.charAt(0).toUpperCase() + c.slice(1));
      }
    } catch (e) {
      // Non-blocking — admin can still type the amount manually
      console.warn("suggest-topup failed:", e?.message || e);
    }
  };

  const submitShippingTopup = async () => {
    const amount = parseFloat(String(topupAmount).replace(",", "."));
    if (!amount || amount <= 0) {
      alert("Inserisci un importo valido maggiore di 0");
      return;
    }
    setTopupSubmitting(true);
    try {
      const access_token = sessionStorage.getItem("access_token");
      const resp = await axios.post(
        `${API}/admin/orders/${order.id}/request-shipping-topup`,
        {
          order_id: order.id,
          delta_amount: amount,
          detected_country: topupCountry || "",
          validity_days: topupDays,
          custom_message: topupMessage || "",
          payment_method: topupPaymentMethod,
        },
        { headers: { Authorization: `Bearer ${access_token}` } }
      );
      // Sanity check: the backend can crash mid-execution (K8s OOM, mongo
      // timeout, email service crash) and return a partial body. If the
      // essential fields are missing, treat as failure and tell the admin.
      const data = resp?.data || {};
      if (!data.short_url || data.amount == null) {
        const detail = JSON.stringify(data).slice(0, 250);
        alert(
          "⚠️ Il server ha risposto con dati incompleti.\n\n" +
          "Probabile crash temporaneo del backend (timeout MongoDB o email).\n" +
          "Riprova tra qualche secondo. Se il problema persiste, controlla i log.\n\n" +
          "Risposta parziale ricevuta: " + detail
        );
        return;
      }
      setTopupResult(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.statusText || e.message;
      const status = e?.response?.status || "no-response";
      alert(`Errore [${status}]: ${detail}\n\nRiprova tra qualche secondo.`);
    } finally {
      setTopupSubmitting(false);
    }
  };

  // Bank-transfer customers don't have a Stripe link to click; admin must
  // manually confirm payment via this dialog when the wire arrives.
  const hasPendingTopup = Array.isArray(order.shipping_topup_requests) &&
    order.shipping_topup_requests.length > 0 &&
    !order.shipping_topup_requests[order.shipping_topup_requests.length - 1]?.paid_manually &&
    (order.payment_status !== "paid");
  const lastTopup = hasPendingTopup
    ? order.shipping_topup_requests[order.shipping_topup_requests.length - 1]
    : null;

  const submitMarkPaid = async () => {
    setMarkPaidSubmitting(true);
    try {
      const access_token = sessionStorage.getItem("access_token");
      await axios.post(
        `${API}/admin/orders/${order.id}/mark-topup-paid`,
        {
          request_index: -1,
          transfer_reference: markPaidRef || "",
          note: markPaidNote || "",
          send_thank_you_email: true,
        },
        { headers: { Authorization: `Bearer ${access_token}` } }
      );
      setShowMarkPaidDialog(false);
      setMarkPaidRef("");
      setMarkPaidNote("");
      if (typeof window !== "undefined" && window.location) window.location.reload();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      alert(`Errore: ${detail}`);
    } finally {
      setMarkPaidSubmitting(false);
    }
  };

  // Auto-bump validity when admin switches to bank transfer (10 days default
  // since SEPA/SWIFT wires take longer than card payments).
  const setTopupMethodWithDefault = (newMethod) => {
    setTopupPaymentMethod(newMethod);
    if (newMethod === "transfer" && topupDays < 7) {
      setTopupDays(10);
    } else if (newMethod === "card" && topupDays > 5) {
      setTopupDays(2);
    }
  };

  const handleDeleteOrder = async () => {
    try {
      const token = sessionStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('Ordine eliminato con successo!');
        window.location.reload();
      } else {
        const error = await response.json();
        alert(`Errore: ${error.detail || 'Impossibile eliminare l\'ordine'}`);
      }
    } catch (error) {
      alert('Errore di connessione durante l\'eliminazione');
    }
    setShowDeleteConfirm(false);
  };

  return (
    <div className="border-t border-gray-200 pt-4">
      <OrderEmailButton order={order} />
      {/* Bottone Stampa sempre visibile */}
      <div className="mb-3 flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handlePrintOrder}
          className="border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          🖨️ Stampa Ordine
        </Button>
        {onDuplicate && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDuplicate(order)}
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="duplicate-order-btn"
          >
            📋 Duplica Ordine
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => { setShowPacklinkModal(true); setPacklinkParamsSubmitted(false); setPacklinkServices([]); setPacklinkError(""); }}
          className="border-purple-300 text-purple-700 hover:bg-purple-50"
          data-testid="packlink-btn"
        >
          📦 Crea Etichetta
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={openEditCustomerDialog}
          className="border-amber-300 text-amber-700 hover:bg-amber-50"
          data-testid="edit-customer-info-btn"
        >
          ✏️ Modifica Cliente
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={openEditItemsDialog}
          className="border-teal-300 text-teal-700 hover:bg-teal-50"
          data-testid="edit-order-items-btn"
        >
          🛒 Modifica Articoli
        </Button>
      </div>

      {/* Edit Customer Info Modal */}
      {showEditCustomerDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[100] flex items-center justify-center p-4" data-testid="edit-customer-modal">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">✏️ Modifica Dati Cliente</h3>
              <button onClick={() => setShowEditCustomerDialog(false)} className="text-gray-500 hover:text-gray-700 text-xl">✕</button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Stai modificando i dati di spedizione dell&apos;ordine <code className="bg-gray-100 px-1 rounded">{order.id?.substring(0, 8)}</code>. Le modifiche aggiornano solo questo ordine (non l&apos;account cliente).
            </p>
            <div className="space-y-3">
              {[
                { key: "name", label: "Nome e Cognome", placeholder: "Es: Mario Rossi" },
                { key: "email", label: "Email", placeholder: "Es: mario@example.com", type: "email" },
                { key: "phone", label: "Telefono", placeholder: "Es: +39 333 1234567" },
                { key: "address", label: "Indirizzo", placeholder: "Es: Via Roma 10" },
                { key: "city", label: "Città", placeholder: "Es: Napoli" },
                { key: "zipCode", label: "CAP", placeholder: "Es: 80100" },
                { key: "country", label: "Zona spedizione (regola)", placeholder: "italia / europa / international" },
                { key: "specificCountry", label: "Paese specifico", placeholder: "Es: Italia, Francia, USA…" },
                { key: "notes", label: "Note", placeholder: "Note interne (opzionale)" },
              ].map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{f.label}</label>
                  <Input
                    type={f.type || "text"}
                    value={editCustForm[f.key] || ""}
                    onChange={(e) => setEditCustForm({ ...editCustForm, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    data-testid={`edit-customer-${f.key}`}
                    className="w-full"
                  />
                </div>
              ))}
            </div>
            {editCustError && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded p-2 text-red-700 text-sm" data-testid="edit-customer-error">
                ❌ {editCustError}
              </div>
            )}
            <div className="flex gap-2 mt-5">
              <Button
                variant="outline"
                onClick={() => setShowEditCustomerDialog(false)}
                disabled={editCustSubmitting}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={submitEditCustomer}
                disabled={editCustSubmitting}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="edit-customer-save-btn"
              >
                {editCustSubmitting ? "Salvataggio…" : "💾 Salva Modifiche"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Order Items Modal */}
      {showEditItemsDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[100] flex items-center justify-center p-4" data-testid="edit-items-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-bold text-gray-900">🛒 Modifica Articoli Ordine</h3>
              <button onClick={() => setShowEditItemsDialog(false)} className="text-gray-500 hover:text-gray-700 text-xl">✕</button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Ordine <code className="bg-gray-100 px-1 rounded">{order.id?.substring(0, 8)}</code> — rimuovi, aggiungi o cambia quantità.
              Lo stock viene riallineato automaticamente (anche su eBay). Premi 🎁 su un articolo per renderlo <b>omaggio a €0</b> (non altera il totale).
            </p>

            {/* Current items */}
            <div className="space-y-2 mb-4" data-testid="edit-items-list">
              {editItems.length === 0 && (
                <p className="text-sm text-red-600">Nessun articolo — aggiungine almeno uno.</p>
              )}
              {editItems.map((it, idx) => (
                <div key={it.article_id} className={`flex items-center gap-2 border rounded-lg px-3 py-2 ${it.price === 0 && it._origPrice ? "border-pink-300 bg-pink-50" : "border-gray-200"}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {it.title}
                      {it.price === 0 && it._origPrice ? (
                        <span className="ml-2 text-[10px] font-bold text-pink-700 bg-pink-100 border border-pink-300 rounded-full px-2 py-0.5 align-middle" data-testid={`edit-item-gift-badge-${idx}`}>🎁 OMAGGIO</span>
                      ) : null}
                    </p>
                    <p className="text-xs text-gray-500">
                      {it.price === 0 && it._origPrice ? (
                        <><span className="line-through">€{Number(it._origPrice).toFixed(2)}</span> <span className="font-semibold text-pink-700">€0.00 cad.</span></>
                      ) : (
                        <>€{(it.price || 0).toFixed(2)} cad.</>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditItems(editItems.map((x, i) => i === idx ? { ...x, quantity: Math.max(1, x.quantity - 1) } : x))}
                      className="w-7 h-7 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 font-bold"
                      data-testid={`edit-item-qty-minus-${idx}`}
                    >−</button>
                    <span className="w-8 text-center text-sm font-semibold" data-testid={`edit-item-qty-${idx}`}>{it.quantity}</span>
                    <button
                      onClick={() => setEditItems(editItems.map((x, i) => i === idx ? { ...x, quantity: x.quantity + 1 } : x))}
                      className="w-7 h-7 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 font-bold"
                      data-testid={`edit-item-qty-plus-${idx}`}
                    >+</button>
                  </div>
                  <button
                    onClick={() => setEditItems(editItems.map((x, i) => i === idx
                      ? (x.price === 0 && x._origPrice
                          ? { ...x, price: x._origPrice, _origPrice: undefined }
                          : { ...x, _origPrice: x.price, price: 0 })
                      : x))}
                    className={`text-lg leading-none px-1 rounded ${it.price === 0 && it._origPrice ? "bg-pink-200" : "hover:bg-pink-100"}`}
                    title={it.price === 0 && it._origPrice ? "Ripristina prezzo originale" : "Rendi omaggio (€0 — non altera il totale)"}
                    data-testid={`edit-item-gift-${idx}`}
                  >🎁</button>
                  <span className="w-20 text-right text-sm font-semibold">€{((it.price || 0) * it.quantity).toFixed(2)}</span>
                  <button
                    onClick={() => setEditItems(editItems.filter((_, i) => i !== idx))}
                    className="text-red-500 hover:text-red-700 ml-1"
                    title="Rimuovi articolo"
                    data-testid={`edit-item-remove-${idx}`}
                  >🗑️</button>
                </div>
              ))}
            </div>

            {/* Add article from catalog */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1">➕ Aggiungi articolo dal catalogo</label>
              <Input
                type="text"
                value={editItemsSearch}
                onChange={(e) => setEditItemsSearch(e.target.value)}
                placeholder="Cerca per titolo…"
                data-testid="edit-items-search"
                className="w-full"
              />
              {editItemsSearch.trim().length >= 2 && (
                <div className="mt-1 border border-gray-200 rounded-lg divide-y max-h-48 overflow-y-auto" data-testid="edit-items-search-results">
                  {editItemsCatalog
                    .filter((a) =>
                      (a.title || "").toLowerCase().includes(editItemsSearch.trim().toLowerCase()) &&
                      !editItems.some((it) => it.article_id === a.id)
                    )
                    .slice(0, 8)
                    .map((a) => (
                      <div key={a.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 truncate">{a.title}</p>
                          <p className="text-xs text-gray-500">
                            €{(a.price || 0).toFixed(2)} · stock: {a.quantity_stock ?? 0}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={(a.quantity_stock ?? 0) < 1}
                          onClick={() => {
                            setEditItems([...editItems, { article_id: a.id, quantity: 1, title: a.title, price: a.price || 0 }]);
                            setEditItemsSearch("");
                          }}
                          className="border-teal-300 text-teal-700 hover:bg-teal-50"
                          data-testid={`edit-items-add-${a.id}`}
                        >
                          {(a.quantity_stock ?? 0) < 1 ? "Esaurito" : "➕ Aggiungi"}
                        </Button>
                      </div>
                    ))}
                  {editItemsCatalog.filter((a) =>
                    (a.title || "").toLowerCase().includes(editItemsSearch.trim().toLowerCase()) &&
                    !editItems.some((it) => it.article_id === a.id)
                  ).length === 0 && (
                    <p className="px-3 py-2 text-sm text-gray-500">Nessun articolo trovato</p>
                  )}
                </div>
              )}
            </div>

            {/* Totals summary */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1" data-testid="edit-items-totals">
              <div className="flex justify-between text-gray-600">
                <span>Articoli ({editItems.reduce((s, i) => s + i.quantity, 0)})</span>
                <span>€{editItems.reduce((s, i) => s + (i.price || 0) * i.quantity, 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Spedizione</span>
                <span>€{(order.shipping_cost || 0).toFixed(2)}</span>
              </div>
              {(order.discount_amount || 0) > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Sconto</span>
                  <span>−€{(order.discount_amount || 0).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-gray-900 border-t border-gray-300 pt-1">
                <span>Nuovo totale</span>
                <span data-testid="edit-items-new-total">€{editItemsNewTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Pagato dal cliente</span>
                <span>€{(order.total_amount || 0).toFixed(2)}</span>
              </div>
              {Math.abs(editItemsNewTotal - (order.total_amount || 0)) >= 0.01 ? (
                <div
                  className={`flex justify-between font-semibold ${editItemsNewTotal > (order.total_amount || 0) ? "text-red-600" : "text-orange-600"}`}
                  data-testid="edit-items-difference"
                >
                  <span>{editItemsNewTotal > (order.total_amount || 0) ? "⚠️ Da richiedere al cliente" : "⚠️ Da rimborsare al cliente"}</span>
                  <span>€{Math.abs(editItemsNewTotal - (order.total_amount || 0)).toFixed(2)}</span>
                </div>
              ) : (
                <div className="flex justify-between font-semibold text-green-600" data-testid="edit-items-difference">
                  <span>✅ Nessuna differenza di prezzo</span>
                  <span>€0.00</span>
                </div>
              )}
            </div>

            {editItemsError && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded p-2 text-red-700 text-sm" data-testid="edit-items-error">
                ❌ {editItemsError}
              </div>
            )}
            <div className="flex gap-2 mt-5">
              <Button
                variant="outline"
                onClick={() => setShowEditItemsDialog(false)}
                disabled={editItemsSubmitting}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={submitEditItems}
                disabled={editItemsSubmitting || editItems.length === 0}
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="edit-items-save-btn"
              >
                {editItemsSubmitting ? "Salvataggio…" : "💾 Salva Articoli"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Packlink Services Modal */}
      {showPacklinkModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPacklinkModal(false)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">📦 Packlink PRO — Scegli Corriere</h3>
              <button onClick={() => setShowPacklinkModal(false)} className="text-gray-500 hover:text-gray-700 text-xl">&times;</button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Destinazione: <strong>{order.customer_info?.city}</strong>, {(() => {
                const ci = order.customer_info || {};
                let name = ci.specificCountry || '';
                if (!name && ci.country && shippingRules) {
                  const rule = shippingRules.find(r => r.id === ci.country);
                  if (rule) name = rule.zone === 'national' ? 'Italia' : rule.country;
                }
                return name || ci.country || '';
              })()} — {order.customer_info?.zipCode || order.customer_info?.postal_code || (() => {
                const city = order.customer_info?.city || '';
                const m = city.match(/\b(\d{2}[-]?\d{3})\b|\b(\d{5})\b|\b(\d{4,6})\b/);
                return m ? (m[1]||m[2]||m[3]) : '';
              })()}
            </p>
            {!packlinkParamsSubmitted ? (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                  <strong>📏 Inserisci dimensioni e peso reali del pacco</strong> prima di cercare i corrieri.
                  I valori qui sotto sono una stima — modificali con i dati effettivi del tuo scatolo.
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Peso (kg)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={parcelWeight}
                      onChange={(e) => setParcelWeight(e.target.value)}
                      data-testid="packlink-input-weight"
                      className="w-full px-3 py-2 border border-gray-300 rounded text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Larghezza (cm)</label>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      value={parcelWidth}
                      onChange={(e) => setParcelWidth(e.target.value)}
                      data-testid="packlink-input-width"
                      className="w-full px-3 py-2 border border-gray-300 rounded text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Altezza (cm)</label>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      value={parcelHeight}
                      onChange={(e) => setParcelHeight(e.target.value)}
                      data-testid="packlink-input-height"
                      className="w-full px-3 py-2 border border-gray-300 rounded text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lunghezza (cm)</label>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      value={parcelLength}
                      onChange={(e) => setParcelLength(e.target.value)}
                      data-testid="packlink-input-length"
                      className="w-full px-3 py-2 border border-gray-300 rounded text-gray-900"
                    />
                  </div>
                </div>
                <Button
                  onClick={() => { setPacklinkParamsSubmitted(true); fetchPacklinkServices(); }}
                  data-testid="packlink-search-services-btn"
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                >
                  🔍 Cerca Corrieri Disponibili
                </Button>
              </div>
            ) : packlinkLoading ? (
              <div className="text-center py-8 text-gray-500">Caricamento tariffe corrieri...</div>
            ) : packlinkError ? (
              <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-sm mb-4">
                {packlinkError}
                <div className="mt-2 flex gap-3 items-center">
                  <button onClick={() => setPacklinkParamsSubmitted(false)} className="text-purple-600 underline text-sm" data-testid="packlink-change-params-btn">← Cambia peso/dimensioni</button>
                  <a href={`https://pro.packlink.com/private/shipments/create`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-sm">Apri Packlink PRO manualmente</a>
                </div>
              </div>
            ) : packlinkServices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>Nessun corriere disponibile con peso <strong>{parcelWeight} kg</strong> e dimensioni <strong>{parcelWidth}×{parcelHeight}×{parcelLength} cm</strong>.</p>
                <p className="text-xs mt-2">Prova con misure più piccole o usa Packlink PRO direttamente.</p>
                <div className="mt-3 flex gap-3 items-center justify-center">
                  <button onClick={() => setPacklinkParamsSubmitted(false)} className="text-purple-600 underline text-sm" data-testid="packlink-change-params-btn-empty">← Cambia peso/dimensioni</button>
                  <a href="https://pro.packlink.com/private/shipments/create" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-sm">Apri Packlink PRO manualmente</a>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-green-50 border border-green-200 rounded p-2 text-xs text-green-800 flex justify-between items-center">
                  <span>✅ {packlinkServices.length} corrieri trovati per {parcelWeight} kg, {parcelWidth}×{parcelHeight}×{parcelLength} cm</span>
                  <button onClick={() => setPacklinkParamsSubmitted(false)} className="text-purple-600 underline" data-testid="packlink-change-params-btn-success">Cambia misure</button>
                </div>
                {packlinkServicesCheapest10.map(service => (
                  <div key={service.id} className="border rounded-lg p-3 hover:border-purple-400 transition-colors">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-semibold text-sm">{service.carrier_name}</span>
                        <span className="text-gray-500 text-xs ml-2">{service.name}</span>
                        <span className="text-gray-400 text-xs ml-2">{service.transit_time || ''}</span>
                        {service.dropoff && <span className="ml-2 bg-yellow-100 text-yellow-700 text-xs px-1.5 py-0.5 rounded">Drop-off</span>}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-green-600">€{parseFloat(service.base_price || 0).toFixed(2)}</span>
                        <Button
                          size="sm"
                          disabled={packlinkCreating}
                          onClick={() => createPacklinkShipment(service.id, `${service.carrier_name} - ${service.name}`)}
                          className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                        >
                          {packlinkCreating ? '...' : 'Scegli'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">{order.status === 'pending' && (
          <>
            <Button
              size="sm"
              onClick={() => handleStatusChange('confirmed')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              ✅ Conferma Ordine
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => openTopupDialog()}
              className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
              title="Invia al cliente un link di pagamento per integrare il costo spedizione"
              data-testid="open-topup-dialog-btn"
            >
              📨 Richiedi Integrazione Spedizione
            </Button>
            {hasPendingTopup && lastTopup?.payment_method === "transfer" && (
              <Button
                size="sm"
                onClick={() => setShowMarkPaidDialog(true)}
                className="bg-green-600 hover:bg-green-700"
                title={`Bonifico atteso: €${(lastTopup.amount || 0).toFixed(2)}`}
                data-testid="mark-topup-paid-btn"
              >
                ✅ Bonifico Ricevuto (€{(lastTopup.amount || 0).toFixed(2)})
              </Button>
            )}
            <Button
              size="sm"
              variant="destructive"
              onClick={() => handleStatusChange('cancelled')}
            >
              ❌ Annulla Ordine
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              🗑️ Elimina
            </Button>
          </>
        )}
        
        {order.status === 'confirmed' && (
          <>
            <Button
              size="sm"
              onClick={() => handleStatusChange('shipped')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              📦 Segna come Spedito
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => handleStatusChange('cancelled')}
            >
              ❌ Annulla Ordine
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              🗑️ Elimina
            </Button>
          </>
        )}
        
        {order.status === 'partially_shipped' && (
          <>
            <Button
              size="sm"
              onClick={() => handleStatusChange('shipped')}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid={`ship-rest-btn-${order.id}`}
            >
              📦 Spedisci il resto
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              🗑️ Elimina
            </Button>
          </>
        )}

        {order.status === 'cancelled' && (
          <>
            <div className="text-red-600 font-semibold">
              🚫 Ordine Annullato
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              🗑️ Elimina
            </Button>
          </>
        )}
        
        {order.status === 'shipped' && (
          <>
            <Button
              size="sm"
              onClick={() => handleStatusChange('delivered')}
              className="bg-green-600 hover:bg-green-700"
            >
              ✓ Segna come Consegnato
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleSendReviewReminder}
              disabled={reviewSending || reviewSent}
              data-testid="send-review-reminder-shipped"
              className={reviewSent ? "border-green-300 text-green-600" : "border-amber-300 text-amber-700 hover:bg-amber-50"}
            >
              {reviewSending ? "Invio..." : reviewSent ? "Inviato!" : `Chiedi Recensione${reminderCount > 0 ? ` (${reminderCount})` : ""}`}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              🗑️ Elimina
            </Button>
          </>
        )}
        
        {(order.status === 'delivered' || order.status === 'cancelled') && (
          <>
            {order.status === 'delivered' && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleSendReviewReminder}
                disabled={reviewSending || reviewSent}
                data-testid="send-review-reminder-delivered"
                className={reviewSent ? "border-green-300 text-green-600" : "border-amber-300 text-amber-700 hover:bg-amber-50"}
              >
                {reviewSending ? "Invio..." : reviewSent ? "Inviato!" : `Chiedi Recensione${reminderCount > 0 ? ` (${reminderCount})` : ""}`}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              🗑️ Elimina
            </Button>
          </>
        )}
      </div>

      {showTrackingInput && (
        <ShipOrderDialog
          order={order}
          onCancel={() => setShowTrackingInput(false)}
          onDone={(res) => {
            setShowTrackingInput(false);
            setShipResult(res.status === 'shipped' ? '✅ Ordine spedito completamente' : `📦 Spedizione parziale registrata: restano ${(res.remaining || []).reduce((a, r) => a + r.quantity, 0)} pezzi da spedire`);
            if (onRefresh) onRefresh();
          }}
        />
      )}
      {shipResult && <p className="text-sm font-semibold text-purple-800 mb-3" data-testid={`ship-result-${order.id}`}>{shipResult}</p>}

      {showDeleteConfirm && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
          <h4 className="font-semibold text-red-900 mb-2">🗑️ Conferma Eliminazione</h4>
          <p className="text-sm text-red-700 mb-3">
            Sei sicuro di voler <strong>eliminare definitivamente</strong> questo ordine? 
            <br />Questa azione <strong>non può essere annullata</strong>.
          </p>
          <div className="flex space-x-2">
            <Button
              size="sm"
              variant="destructive"
              onClick={handleDeleteOrder}
            >
              🗑️ Sì, Elimina Definitivamente
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Annulla
            </Button>
          </div>
        </div>
      )}

      {/* ---------- Cancel order dialog ---------- */}
      {showCancelDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="cancel-order-dialog"
          onClick={() => !cancelSubmitting && setShowCancelDialog(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              ❌ Annulla Ordine #{(order.id || "").slice(-6).toUpperCase()}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Stai per annullare l'ordine di <strong>{order.customer_info?.name || "Cliente"}</strong>.
              Inserisci un motivo (verrà mostrato al cliente se invii l'email).
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo (opzionale, max 500 caratteri)
            </label>
            <textarea
              data-testid="cancel-reason-input"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value.slice(0, 500))}
              placeholder="Es: Pagamento Satispay non ricevuto entro 24h. Riprova o contattaci a amm@edicolavillaricca.com"
              rows={4}
              className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-3"
              disabled={cancelSubmitting}
            />

            <label className="flex items-center gap-2 mb-4 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="cancel-send-email-checkbox"
                checked={cancelSendEmail}
                onChange={(e) => setCancelSendEmail(e.target.checked)}
                disabled={cancelSubmitting}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <span className="text-sm text-gray-700">
                📧 Invia email di notifica al cliente
                <span className="block text-xs text-gray-500">
                  L'email viene tradotta automaticamente in base al paese del cliente (IT/EN/DE/FR).
                </span>
              </span>
            </label>

            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowCancelDialog(false)}
                disabled={cancelSubmitting}
                data-testid="cancel-dialog-abort"
              >
                Indietro
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={confirmCancellation}
                disabled={cancelSubmitting}
                data-testid="cancel-dialog-confirm"
              >
                {cancelSubmitting ? "Annullamento..." : "❌ Conferma Annullamento"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Shipping top-up request dialog ---------- */}
      {showTopupDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="topup-dialog"
          onClick={() => !topupSubmitting && setShowTopupDialog(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              📨 Richiesta integrazione spedizione
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Cliente: <strong>{order.customer_info?.name || "—"}</strong> ({order.customer_info?.email})
            </p>

            {!topupResult ? (
              <>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Metodo di pagamento *
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setTopupMethodWithDefault("card")}
                      className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium ${
                        topupPaymentMethod === "card"
                          ? "bg-cyan-50 border-cyan-500 text-cyan-900"
                          : "bg-white border-gray-300 text-gray-700"
                      }`}
                      data-testid="topup-method-card"
                      disabled={topupSubmitting}
                    >
                      💳 Carta (Stripe)
                      <div className="text-xs text-gray-500 mt-1">Veloce, scadenza 2gg default</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTopupMethodWithDefault("transfer")}
                      className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium ${
                        topupPaymentMethod === "transfer"
                          ? "bg-amber-50 border-amber-500 text-amber-900"
                          : "bg-white border-gray-300 text-gray-700"
                      }`}
                      data-testid="topup-method-transfer"
                      disabled={topupSubmitting}
                    >
                      🏦 Bonifico Bancario
                      <div className="text-xs text-gray-500 mt-1">SEPA/SWIFT, scadenza 10gg default</div>
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Importo da integrare (€) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                    placeholder="Es: 16.00"
                    className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-cyan-500"
                    data-testid="topup-amount-input"
                    disabled={topupSubmitting}
                  />
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Paese reale del cliente (mostrato nell'email)
                  </label>
                  <input
                    type="text"
                    value={topupCountry}
                    onChange={(e) => setTopupCountry(e.target.value)}
                    placeholder="Es: Germania, Francia, Regno Unito..."
                    className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-cyan-500"
                    data-testid="topup-country-input"
                    disabled={topupSubmitting}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Lascia vuoto se vuoi inviare una richiesta generica senza menzionare il paese
                  </p>
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scadenza (giorni)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={topupDays}
                    onChange={(e) => setTopupDays(Math.max(1, Math.min(30, parseInt(e.target.value) || 2)))}
                    className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-cyan-500"
                    data-testid="topup-days-input"
                    disabled={topupSubmitting}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Dopo la scadenza il link non sarà più utilizzabile.
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Messaggio personalizzato (opzionale)
                  </label>
                  <textarea
                    rows={3}
                    value={topupMessage}
                    onChange={(e) => setTopupMessage(e.target.value.slice(0, 500))}
                    placeholder="Es: La spedizione per la Germania costa €25 invece di €9. Grazie per la comprensione."
                    className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-cyan-500"
                    data-testid="topup-message-input"
                    disabled={topupSubmitting}
                  />
                </div>

                <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-900 mb-4">
                  ℹ️ Verrà inviata un'email automatica al cliente nella sua lingua (IT/EN/DE/FR auto-detect) con il link di pagamento Stripe.
                  L'ordine resta in stato pending — potrai annullarlo manualmente in seguito se il cliente non paga entro la scadenza.
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowTopupDialog(false)}
                    disabled={topupSubmitting}
                  >
                    Indietro
                  </Button>
                  <Button
                    size="sm"
                    onClick={submitShippingTopup}
                    disabled={topupSubmitting || !topupAmount}
                    className="bg-cyan-600 hover:bg-cyan-700"
                    data-testid="topup-submit-btn"
                  >
                    {topupSubmitting ? "Invio in corso..." : "📨 Genera link e invia email"}
                  </Button>
                </div>
              </>
            ) : (
              <div data-testid="topup-success">
                <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-4">
                  <p className="text-green-800 font-semibold mb-1">✅ Link generato!</p>
                  {topupResult.email_sent ? (
                    <p className="text-green-700 text-sm">📧 Email inviata a <strong>{topupResult.customer_email}</strong></p>
                  ) : (
                    <p className="text-amber-700 text-sm">⚠️ Email NON inviata — invia manualmente il link sotto.</p>
                  )}
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Link breve (per WhatsApp/SMS)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={topupResult.short_url || ""}
                      className="flex-1 border border-gray-300 rounded-lg p-2 text-sm bg-gray-50"
                    />
                    <Button
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(topupResult.short_url || "");
                        alert("Link copiato!");
                      }}
                      className="bg-cyan-600 hover:bg-cyan-700"
                    >
                      📋 Copia
                    </Button>
                  </div>
                </div>

                <div className="text-sm text-gray-600 mb-4">
                  <p><strong>Importo:</strong> € {Number(topupResult.amount || 0).toFixed(2)}</p>
                  <p><strong>Causale:</strong> {topupResult.causale || "—"}</p>
                  <p><strong>Scadenza:</strong> {(() => {
                    const d = topupResult.expires_at ? new Date(topupResult.expires_at) : null;
                    return d && !isNaN(d.getTime()) ? d.toLocaleDateString("it-IT") : "—";
                  })()}</p>
                </div>

                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={() => setShowTopupDialog(false)}
                    className="bg-gray-600 hover:bg-gray-700"
                  >
                    Chiudi
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------- Mark topup as paid (bank transfer received) ---------- */}
      {showMarkPaidDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="mark-paid-dialog"
          onClick={() => !markPaidSubmitting && setShowMarkPaidDialog(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              ✅ Conferma ricezione bonifico
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Stai marcando come ricevuta l'integrazione spedizione di
              {" "}<strong>€ {lastTopup ? lastTopup.amount?.toFixed(2) : "?"}</strong> per l'ordine
              {" "}#{(order.id || "").slice(-6).toUpperCase()}.
              <br />
              Verrà inviata un'email di ringraziamento al cliente nella sua lingua e l'ordine sarà marcato come pagato.
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Riferimento bonifico (opzionale)
            </label>
            <input
              type="text"
              value={markPaidRef}
              onChange={(e) => setMarkPaidRef(e.target.value)}
              placeholder="Es: CRO123456, IBAN ultimi 4: 1234"
              className="w-full border border-gray-300 rounded-lg p-2 text-sm mb-3"
              data-testid="mark-paid-ref-input"
              disabled={markPaidSubmitting}
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nota interna (opzionale)
            </label>
            <textarea
              rows={2}
              value={markPaidNote}
              onChange={(e) => setMarkPaidNote(e.target.value.slice(0, 500))}
              placeholder="Note visibili solo all'admin"
              className="w-full border border-gray-300 rounded-lg p-2 text-sm mb-4"
              data-testid="mark-paid-note-input"
              disabled={markPaidSubmitting}
            />

            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowMarkPaidDialog(false)}
                disabled={markPaidSubmitting}
              >
                Indietro
              </Button>
              <Button
                size="sm"
                onClick={submitMarkPaid}
                disabled={markPaidSubmitting}
                className="bg-green-600 hover:bg-green-700"
                data-testid="mark-paid-confirm"
              >
                {markPaidSubmitting ? "Conferma..." : "✅ Conferma"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
